/**
 * Delete, from every React client (block 04).
 *
 * **Hard delete, not trash** (override 1): there is no `useRestoreNook` and no
 * `useTrash` to invalidate, so this hook's whole job is to take the row out of
 * every cached list and forget its detail.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getNookClient } from "../client.js";
import { queryKeys } from "../query-keys.js";
export function useDeleteNook() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => getNookClient().deleteNook(id),
        // Safe to retry precisely because the backend answers `204` for a nook that
        // is already gone (override 3) — a retry after a lost response succeeds
        // rather than resurrecting the card by rolling the removal back.
        retry: 1,
        async onMutate(id) {
            await queryClient.cancelQueries({ queryKey: queryKeys.nooks.lists() });
            const snapshot = queryClient.getQueriesData({
                queryKey: queryKeys.nooks.lists(),
            });
            queryClient.setQueriesData({ queryKey: queryKeys.nooks.lists() }, (current) => current && {
                ...current,
                pages: current.pages.map((page) => ({
                    ...page,
                    items: page.items.filter((item) => item.id !== id),
                })),
            });
            return { snapshot };
        },
        onError(_error, _id, context) {
            for (const [key, data] of context?.snapshot ?? []) {
                queryClient.setQueryData(key, data);
            }
        },
        // Removed rather than invalidated: refetching a detail that is gone would
        // only earn a `404`, and the panel has already navigated away.
        onSuccess(_result, id) {
            queryClient.removeQueries({ queryKey: queryKeys.nooks.detail(id) });
        },
        onSettled() {
            void queryClient.invalidateQueries({ queryKey: queryKeys.nooks.lists() });
            void queryClient.invalidateQueries({ queryKey: queryKeys.nooks.sites() });
        },
    });
}
//# sourceMappingURL=use-delete-nook.js.map